import React, { useEffect, useMemo, useState } from 'react';
import { Phone, MessageSquare, Save, Loader2, AlertTriangle } from 'lucide-react';
import { getAuth } from 'firebase/auth';
import { toast } from 'sonner';
import { useCompany } from '../../context/CompanyContext';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Switch } from '../../components/ui/switch';
import { Textarea } from '../../components/ui/textarea';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '../../components/ui/select';
import QuoteFollowupSettings from './QuoteFollowupSettings';
import CallForwardingSettings from './CallForwardingSettings';
import PhoneVerificationCard from '../../components/clicktocall/PhoneVerificationCard';
import InboundWebRTCSettings from './InboundWebRTCSettings';

const API = process.env.REACT_APP_BACKEND_URL || '';

// Curated TZ list — matches TimezoneSettings for consistency
const TIMEZONES = [
  { value: 'America/New_York',     label: 'Eastern — New York' },
  { value: 'America/Chicago',      label: 'Central — Chicago' },
  { value: 'America/Denver',       label: 'Mountain — Denver' },
  { value: 'America/Phoenix',      label: 'Mountain (no DST) — Phoenix' },
  { value: 'America/Los_Angeles',  label: 'Pacific — Los Angeles' },
  { value: 'America/Anchorage',    label: 'Alaska — Anchorage' },
  { value: 'Pacific/Honolulu',     label: 'Hawaii — Honolulu' },
  { value: 'UTC',                  label: 'UTC' },
];

const DEFAULT_TEMPLATE =
  "Hi {firstName} — sorry we missed you, this is {company}. Reply here and we'll get right back to you.";

const DEFAULT_VOICE_FALLBACK =
  "Hi! Sorry we missed your call. We just sent you a text — reply there and someone will get right back to you. Thanks!";

const DEFAULT_VOICEMAIL_GREETING =
  "Sorry we missed your call. Please leave a message after the tone — your message will be recorded for follow-up. Thanks!";

const GREETING_MAX = 150;

const VF_MODE_OPTIONS = [
  { value: 'text_only', label: 'Text-only fallback', help: 'Play a short message telling the caller to check their texts. (Default)' },
  { value: 'voicemail', label: 'Voicemail (record up to 60s)', help: 'Play your greeting and let the caller leave a voicemail. The recording attaches to the Inbox thread.' },
  { value: 'off',       label: 'Off — just hang up', help: 'No audio played. The caller still gets the missed-call text-back.' },
];

const CommunicationSettings = () => {
  const { companyId } = useCompany();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving]   = useState(false);
  const [enabled, setEnabled] = useState(true);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [hours, setHours] = useState({ start: '08:00', end: '18:00', timezone: 'America/New_York', weekdaysOnly: false });
  const [cooldown, setCooldown] = useState(120);
  const [vfMode, setVfMode] = useState('text_only');
  const [vfMessage, setVfMessage] = useState(DEFAULT_VOICE_FALLBACK);
  const [vfGreeting, setVfGreeting] = useState(DEFAULT_VOICEMAIL_GREETING);
  const [savedSnapshot, setSavedSnapshot] = useState(null);

  // Initial load
  useEffect(() => {
    if (!companyId) return;
    let cancelled = false;
    (async () => {
      try {
        const token = await getAuth().currentUser?.getIdToken(false);
        const res = await fetch(`${API}/api/settings/missed-call`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        const payload = await res.json();
        if (cancelled) return;
        if (res.ok && payload?.settings) {
          const s = payload.settings;
          setEnabled(!!s.enabled);
          setTemplate(s.replyTemplate || DEFAULT_TEMPLATE);
          setHours({
            start: s.businessHours?.start || '08:00',
            end:   s.businessHours?.end   || '18:00',
            timezone: s.businessHours?.timezone || 'America/New_York',
            weekdaysOnly: !!s.businessHours?.weekdaysOnly,
          });
          setCooldown(Number(s.cooldownMinutes) || 120);
          // Phase 2.7 — tri-state. Backwards-compat with legacy { enabled }.
          const vf = s.voiceFallback || {};
          let mode = vf.mode;
          if (!mode) {
            if (vf.enabled === false) mode = 'off';
            else mode = 'text_only';
          }
          setVfMode(mode);
          setVfMessage(vf.message || DEFAULT_VOICE_FALLBACK);
          setVfGreeting(vf.greeting || DEFAULT_VOICEMAIL_GREETING);
          setSavedSnapshot(JSON.stringify(s));
        }
      } catch (err) {
        console.error('[CommunicationSettings] load failed:', err);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [companyId]);

  const isDirty = useMemo(() => {
    if (!savedSnapshot) return true;
    try {
      const parsed = JSON.parse(savedSnapshot);
      const savedVf = parsed.voiceFallback || {};
      const savedMode = savedVf.mode || (savedVf.enabled === false ? 'off' : 'text_only');
      return (
        parsed.enabled !== enabled ||
        parsed.replyTemplate !== template ||
        parsed.cooldownMinutes !== cooldown ||
        (parsed.businessHours?.start || '08:00') !== hours.start ||
        (parsed.businessHours?.end || '18:00') !== hours.end ||
        (parsed.businessHours?.timezone || 'America/New_York') !== hours.timezone ||
        !!parsed.businessHours?.weekdaysOnly !== hours.weekdaysOnly ||
        savedMode !== vfMode ||
        (savedVf.message || DEFAULT_VOICE_FALLBACK) !== vfMessage ||
        (savedVf.greeting || DEFAULT_VOICEMAIL_GREETING) !== vfGreeting
      );
    } catch { return true; }
  }, [savedSnapshot, enabled, template, cooldown, hours, vfMode, vfMessage, vfGreeting]);

  const handleSave = async () => {
    if (saving) return;
    setSaving(true);
    try {
      const token = await getAuth().currentUser?.getIdToken(false);
      const res = await fetch(`${API}/api/settings/missed-call`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({
          enabled,
          replyTemplate: template,
          businessHours: hours,
          cooldownMinutes: cooldown,
          voiceFallback: { mode: vfMode, message: vfMessage, greeting: vfGreeting },
        }),
      });
      const payload = await res.json().catch(() => ({}));
      if (res.ok && payload?.success) {
        setSavedSnapshot(JSON.stringify(payload.settings));
        const liveMode = (payload.settings?.voiceFallback?.mode) || 'text_only';
        const liveLabel = {
          off: 'Off',
          text_only: 'Text-only',
          voicemail: 'Voicemail',
        }[liveMode] || liveMode;
        toast.success(`Saved — voice fallback is now: ${liveLabel}`);
      } else {
        toast.error(payload?.detail || 'Failed to save settings');
      }
    } catch (err) {
      console.error('[CommunicationSettings] save failed:', err);
      toast.error('Network error — please try again');
    } finally {
      setSaving(false);
    }
  };

  const previewMessage = template
    .replaceAll('{firstName}', 'Sarah')
    .replaceAll('{company}', 'Crow Cleaning Co');

  // Show the LAST-SAVED mode so the operator can verify the value that
  // is actually live in Firestore (not just whatever the dropdown shows
  // before a save). Critical for debugging "I selected voicemail but
  // text-only is still playing" symptoms.
  const savedVfMode = useMemo(() => {
    if (!savedSnapshot) return null;
    try {
      const parsed = JSON.parse(savedSnapshot);
      const v = parsed.voiceFallback || {};
      if (v.mode) return v.mode;
      if (v.enabled === false) return 'off';
      return 'text_only';
    } catch { return null; }
  }, [savedSnapshot]);

  const savedVfModeLabel = {
    off: 'Off — just hang up',
    text_only: 'Text-only fallback',
    voicemail: 'Voicemail (record up to 60s)',
  }[savedVfMode];

  return (
    <div className="max-w-3xl space-y-6" data-testid="communication-settings-page">
      {/* Page heading */}
      <div className="flex items-start gap-3">
        <div className="w-11 h-11 rounded-xl bg-[rgba(242,107,29,0.1)] flex items-center justify-center shrink-0">
          <MessageSquare className="w-5 h-5 text-[var(--cb-orange)]" />
        </div>
        <div>
          <h2 className="text-2xl font-bold text-secondary">Communication</h2>
          <p className="text-sm text-gray-500">
            Capture missed inbound calls and follow up automatically by SMS.
          </p>
        </div>
      </div>

      {loading ? (
        <div className="flex items-center gap-2 text-sm text-gray-500">
          <Loader2 className="w-4 h-4 animate-spin" /> Loading…
        </div>
      ) : (
        <>
          {/* Missed-call card */}
          <section className="bg-white rounded-xl border border-gray-200 p-6 shadow-sm space-y-5" data-testid="missed-call-card">
            <div className="flex items-start justify-between gap-4">
              <div className="flex items-start gap-3">
                <Phone className="w-5 h-5 text-gray-500 mt-0.5" />
                <div>
                  <h3 className="text-base font-semibold text-secondary">Missed-call text-back</h3>
                  <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                    When a caller hangs up before talking to anyone, automatically text them back so the lead doesn't slip.
                  </p>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <Label htmlFor="missed-call-enabled" className="text-sm">Enabled</Label>
                <Switch
                  id="missed-call-enabled"
                  checked={enabled}
                  onCheckedChange={setEnabled}
                  data-testid="missed-call-enabled-toggle"
                />
              </div>
            </div>

            {/* Template editor */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Auto-reply template</Label>
              <Textarea
                value={template}
                onChange={(e) => setTemplate(e.target.value)}
                rows={3}
                className="text-sm"
                maxLength={500}
                data-testid="missed-call-template-input"
              />
              <div className="flex items-center justify-between text-xs text-gray-500">
                <span>
                  Available placeholders: <code className="bg-gray-100 px-1 rounded">{`{firstName}`}</code>{' '}
                  <code className="bg-gray-100 px-1 rounded">{`{company}`}</code>
                </span>
                <span>{template.length}/500</span>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3" data-testid="missed-call-template-preview">
                <div className="text-[11px] font-semibold uppercase tracking-wider text-gray-400 mb-1">Preview</div>
                <p className="text-sm text-gray-800 whitespace-pre-wrap">{previewMessage}</p>
              </div>
            </div>

            {/* Business hours */}
            <div className="space-y-2">
              <Label className="text-sm font-medium">Business hours</Label>
              <p className="text-xs text-gray-500">
                Outside this window the missed call still lands in your Inbox, but no auto-reply is sent.
              </p>
              <div className="grid grid-cols-2 gap-3 max-w-md">
                <div>
                  <Label htmlFor="missed-call-start" className="text-xs text-gray-600">Start</Label>
                  <Input
                    id="missed-call-start"
                    type="time"
                    value={hours.start}
                    onChange={(e) => setHours((h) => ({ ...h, start: e.target.value }))}
                    data-testid="missed-call-hours-start"
                  />
                </div>
                <div>
                  <Label htmlFor="missed-call-end" className="text-xs text-gray-600">End</Label>
                  <Input
                    id="missed-call-end"
                    type="time"
                    value={hours.end}
                    onChange={(e) => setHours((h) => ({ ...h, end: e.target.value }))}
                    data-testid="missed-call-hours-end"
                  />
                </div>
              </div>
              <div className="flex items-center gap-3 max-w-md">
                <div className="flex-1">
                  <Label className="text-xs text-gray-600">Timezone</Label>
                  <Select
                    value={hours.timezone}
                    onValueChange={(v) => setHours((h) => ({ ...h, timezone: v }))}
                  >
                    <SelectTrigger className="h-9" data-testid="missed-call-tz-trigger">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map((t) => (
                        <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <label className="flex items-center gap-2 mt-5 text-sm shrink-0">
                  <input
                    type="checkbox"
                    checked={hours.weekdaysOnly}
                    onChange={(e) => setHours((h) => ({ ...h, weekdaysOnly: e.target.checked }))}
                    data-testid="missed-call-weekdays-only"
                  />
                  Weekdays only
                </label>
              </div>
            </div>

            {/* Cooldown */}
            <div className="space-y-2 max-w-md">
              <Label htmlFor="missed-call-cooldown" className="text-sm font-medium">
                Per-caller cooldown (minutes)
              </Label>
              <p className="text-xs text-gray-500">
                Don't auto-reply to the same number more than once inside this window.
              </p>
              <Input
                id="missed-call-cooldown"
                type="number"
                min={0}
                max={1440}
                step={5}
                value={cooldown}
                onChange={(e) => setCooldown(Math.max(0, Math.min(1440, Number(e.target.value) || 0)))}
                className="w-32"
                data-testid="missed-call-cooldown-input"
              />
            </div>

            {/* Voice fallback (Phase 2.5 / 2.7) — what to do on leg A when
                no agent answers: play a text-only message, record a voicemail,
                or just hang up. */}
            <div className="space-y-3 pt-2 border-t border-gray-100" data-testid="voice-fallback-block">
              <div>
                <Label className="text-sm font-medium">Voice fallback</Label>
                <p className="text-xs text-gray-500 mt-0.5 max-w-md">
                  Decide what the caller hears when nobody picks up. The missed-call
                  text-back still fires either way.
                </p>
              </div>
              <Select value={vfMode} onValueChange={setVfMode}>
                <SelectTrigger className="h-9 max-w-md" data-testid="voice-fallback-mode-trigger">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {VF_MODE_OPTIONS.map((opt) => (
                    <SelectItem
                      key={opt.value}
                      value={opt.value}
                      data-testid={`voice-fallback-mode-${opt.value}`}
                    >
                      {opt.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {savedVfModeLabel && (
                <div
                  className={`inline-flex items-center gap-1.5 px-2 py-1 rounded text-[11px] font-medium ${
                    savedVfMode === vfMode
                      ? 'bg-emerald-50 text-emerald-700 border border-emerald-200'
                      : 'bg-amber-50 text-amber-800 border border-amber-200'
                  }`}
                  data-testid="voice-fallback-saved-badge"
                >
                  <span>Currently live:</span>
                  <span>{savedVfModeLabel}</span>
                  {savedVfMode !== vfMode && <span>· unsaved change</span>}
                </div>
              )}
              <p className="text-[11px] text-gray-500 max-w-md">
                {VF_MODE_OPTIONS.find((o) => o.value === vfMode)?.help}
              </p>

              {vfMode === 'text_only' && (
                <div data-testid="voice-fallback-text-block">
                  <Label htmlFor="voice-fallback-message" className="text-xs text-gray-600">
                    Message played to caller
                  </Label>
                  <Textarea
                    id="voice-fallback-message"
                    value={vfMessage}
                    onChange={(e) => setVfMessage(e.target.value.slice(0, 300))}
                    rows={3}
                    placeholder={DEFAULT_VOICE_FALLBACK}
                    className="text-sm mt-1"
                    data-testid="voice-fallback-message-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">{vfMessage.length}/300 characters</p>
                </div>
              )}

              {vfMode === 'voicemail' && (
                <div data-testid="voice-fallback-voicemail-block">
                  <Label htmlFor="voice-fallback-greeting" className="text-xs text-gray-600">
                    Greeting played before the beep
                  </Label>
                  <Textarea
                    id="voice-fallback-greeting"
                    value={vfGreeting}
                    onChange={(e) => setVfGreeting(e.target.value.slice(0, GREETING_MAX))}
                    rows={3}
                    placeholder={DEFAULT_VOICEMAIL_GREETING}
                    className="text-sm mt-1"
                    data-testid="voice-fallback-greeting-input"
                  />
                  <p className="text-[11px] text-gray-400 mt-1">{vfGreeting.length}/{GREETING_MAX} characters</p>
                  <p className="text-[11px] text-gray-500 mt-2">
                    Recordings cap at 60 seconds. Anything under 2 seconds is discarded as silence.
                  </p>
                </div>
              )}
            </div>

            {!enabled && (
              <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800" data-testid="missed-call-disabled-hint">
                <AlertTriangle className="w-4 h-4 shrink-0 mt-0.5" />
                <span>
                  Auto-reply is paused. Missed calls will still appear in your Inbox so you can text back manually.
                </span>
              </div>
            )}

            <div className="pt-2">
              <Button
                onClick={handleSave}
                disabled={saving || !isDirty}
                size="sm"
                data-testid="missed-call-save-btn"
              >
                {saving ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {saving ? 'Saving…' : 'Save settings'}
              </Button>
            </div>
          </section>

          {/* Setup hint */}
          <div className="text-xs text-gray-500 bg-gray-50 border border-gray-200 rounded-lg p-3">
            <strong className="text-gray-700">Setup:</strong> point your Telnyx Voice / Call Control App webhook URL to{' '}
            <code className="bg-white px-1 rounded border">/api/telnyx/voice</code> on your KRO domain. Webhook signature
            verification uses Ed25519 — set <code className="bg-white px-1 rounded border">TELNYX_PUBLIC_KEY</code> in
            <code className="bg-white px-1 rounded border">backend/.env</code> from{' '}
            <span className="whitespace-nowrap">Telnyx Portal → Auth → Public Key</span>.
          </div>

          {/* Inbound call forwarding (Phase 1.5) */}
          <CallForwardingSettings />

          {/* Click-to-Call personal-phone verification (Phase 3.1A) */}
          <PhoneVerificationCard />

          {/* Inbound WebRTC presence + per-user opt-in (Phase 4.2a — instrumentation only) */}
          <InboundWebRTCSettings />

          {/* Quote follow-up auto-fire (Phase 1 / A2) */}
          <QuoteFollowupSettings />
        </>
      )}
    </div>
  );
};

export default CommunicationSettings;
